import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { mockData } from '../mockData';
import { Card, CardContent } from './ui/card';

const contactInfo = [
  {
    icon: MapPin,
    title: 'Morada',
    content: mockData.company.address,
    link: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mockData.company.address)}`,
    external: true
  },
  {
    icon: Mail,
    title: 'Email',
    content: mockData.company.email,
    link: `mailto:${mockData.company.email}`
  },
  {
    icon: Phone,
    title: 'Contactos',
    content: (
      <div className="space-y-2">
        <div>
          <div className="font-semibold text-navy">Dra. Márcia Durães</div>
          <a href={`tel:${mockData.team[0].phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors">
            {mockData.team[0].phone}
          </a>
        </div>
        <div>
          <div className="font-semibold text-navy mt-3">Dra. Stéphanie Barros</div>
          <a href={`tel:${mockData.team[1].phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors">
            {mockData.team[1].phone}
          </a>
        </div>
      </div>
    )
  },
  {
    icon: Clock,
    title: 'Horário de Atendimento',
    content: (
      <div className="space-y-1">
        <div>Segunda a Sexta: 09:00 - 18:00</div>
        <div className="text-sm text-navy/60">Sábado e Domingo: Fechado</div>
      </div>
    )
  }
];

const ContactInfoCard = ({ info }) => {
  const Icon = info.icon;
  return (
    <Card className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-gold/20">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <div className="w-14 h-14 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7 text-gold" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-navy text-lg mb-3">{info.title}</h3>
            {info.link && typeof info.content === 'string' ? (
              <a
                href={info.link}
                target={info.external ? '_blank' : undefined}
                rel={info.external ? 'noopener noreferrer' : undefined}
                className="text-navy/70 hover:text-gold transition-colors leading-relaxed"
              >
                {info.content}
              </a>
            ) : (
              <div className="text-navy/70 leading-relaxed">{info.content}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ContactCTA = () => (
  <Card className="bg-gradient-to-br from-navy to-navy/90 border-2 border-gold/20 shadow-xl">
    <CardContent className="p-8 text-center space-y-4">
      <h3 className="font-serif text-2xl font-bold text-white">
        Pronto para uma Consulta?
      </h3>
      <p className="text-white/80 leading-relaxed">
        Entre em contacto connosco através de qualquer um dos meios acima.
        Responderemos o mais breve possível.
      </p>
      <div className="pt-4">
        <a
          href={`mailto:${mockData.company.email}`}
          data-testid="contact-email-cta"
          className="inline-block bg-gold hover:bg-gold/90 text-navy font-semibold px-8 py-4 rounded-sm transition-all duration-300 hover:shadow-xl"
        >
          Enviar Email Agora
        </a>
      </div>
    </CardContent>
  </Card>
);

const ContactMap = () => (
  <div className="lg:sticky lg:top-24">
    <Card className="overflow-hidden border-2 border-gold/20 shadow-xl">
      <CardContent className="p-0">
        <div className="relative">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.5!2d-8.5!3d41.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDEyJzAwLjAiTiA4wrAzMCcwMC4wIlc!5e0!3m2!1spt-PT!2spt!4v1234567890"
            width="100%"
            height="600"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Localização Barros & Durães Advogados"
            className="grayscale hover:grayscale-0 transition-all duration-500"
          ></iframe>
          <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-6 rounded-lg shadow-xl">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h4 className="font-semibold text-navy mb-1">Visite-nos</h4>
                <p className="text-sm text-navy/70 leading-relaxed">
                  {mockData.company.address}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const Contact = () => {
  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-cream via-white to-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-block">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">
              Contactos
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-navy leading-tight">
            Entre em
            <span className="text-gold"> Contacto Connosco</span>
          </h2>
          <p className="text-lg text-navy/70 leading-relaxed">
            Estamos disponíveis para esclarecer as suas dúvidas e prestar o apoio jurídico que necessita.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            {contactInfo.map((info) => (
              <ContactInfoCard key={info.title} info={info} />
            ))}
            <ContactCTA />
          </div>
          <ContactMap />
        </div>
      </div>
    </section>
  );
};

export default Contact;
